import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { WaitlistForm } from '../components/WaitlistForm';
import { WaitlistStats } from '../components/WaitlistStats';
import { useWaitlistCount } from '../hooks/useWaitlistCount';
import { CheckCircle2 } from 'lucide-react';
import { FadeIn } from '../components/animations/FadeIn';
import { SEO } from '../components/SEO';

function About() {
  const [submitted, setSubmitted] = useState(false);
  const { name: currentTier, spots: remainingSpots } = useWaitlistCount();

  return (
    <div className="min-h-screen">
      <SEO 
        title="Om Utlysningslabbet | AI-driven bidragsassistent"
        description="Lär känna teamet bakom Utlysningslabbet och vår vision om att demokratisera tillgången till bidrag och finansieringsmöjligheter med hjälp av artificiell intelligens."
        keywords="utlysningslabbet, AI-assistent, bidragsansökningar, startup finansiering, innovation, forskningsfinansiering, team, vision"
        canonicalUrl="https://utlysningslabbet.com/about"
        ogImage="https://res.cloudinary.com/ds0t8ogko/image/upload/v1745237951/ChatGPT_Image_21_apr._2025_14_19_02_zj6vto.png"
        twitterImage="https://res.cloudinary.com/ds0t8ogko/image/upload/v1745237951/ChatGPT_Image_21_apr._2025_14_19_02_zj6vto.png"
        twitterAlt="Teamet bakom Utlysningslabbet"
      />
      
      {/* Hero Section */}
      <section className="bg-gray-900 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 md:mb-8 leading-tight">
                Det borde vara enklare att hitta och söka rätt finansiering
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                Vår mission är att demokratisera tillgången till bidrag och finansieringsmöjligheter med hjälp av artificiell intelligens.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-bold mb-8">Vår historia</h2>
              <div className="space-y-6 text-base md:text-lg leading-relaxed">
                <p className="text-gray-600">
                  Det började med en frustration som många entreprenörer känner igen - den tidskrävande processen att hitta rätt bidrag och sedan författa ansökningar som uppfyller alla kriterier och använder rätt nyckelord.
                </p>
                <p className="text-gray-600">
                  Vi såg hur många lovande projekt och innovativa idéer aldrig fick chansen att blomstra, helt enkelt för att processen att söka finansiering var för komplex och tidskrävande. Timmar som kunde ha använts till att utveckla produkter och nå kunder gick istället till att navigera genom en labyrint av bidragsansökningar.
                </p>
                <p className="text-gray-600">
                  Det var då vi fick idén att skapa något revolutionerande - en AI-driven copilot som tränats på över 60 000 framgångsrika svenska bidragsansökningar. Genom att analysera denna omfattande datamängd kan vi skapa ett verktyg som inte bara hittar de mest relevanta bidragen för din situation, utan också vägleder dig genom hela ansökningsprocessen. <Link to="/demo" className="text-blue-600 hover:text-blue-700 font-medium">Testa vår demo</Link> för att se hur det fungerar.
                </p>
                <p className="text-gray-600">
                  Vår vision är att demokratisera tillgången till finansiering. Vi tror att briljanta idéer förtjänar att bli verklighet, oavsett grundarens tidigare erfarenhet av bidragsansökningar. Med Utlysningslabbet vid din sida kan du fokusera på det som verkligen betyder något - att bygga och utveckla ditt företag. <Link to="/progress" className="text-blue-600 hover:text-blue-700 font-medium">Se hur långt vi har kommit</Link> i utvecklingen.
                </p>
                <p className="text-gray-600">
                  Låt oss tillsammans förändra hur svenska startups och entreprenörer får tillgång till finansiering. Framtiden är här, och den är tillgänglig för alla. <Link to="/qa" className="text-blue-600 hover:text-blue-700 font-medium">Läs våra vanliga frågor</Link> för att lära dig mer om hur vi kan hjälpa dig.
                </p>
              </div>
            </FadeIn>
            <div className="order-first md:order-last">
              <FadeIn direction="left">
                <img
                  src="https://res.cloudinary.com/ds0t8ogko/image/upload/v1745237951/ChatGPT_Image_21_apr._2025_14_19_02_zj6vto.png"
                  alt="Teamsamarbete"
                  className="rounded-3xl shadow-2xl w-full"
                />
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4">
          {!submitted ? (
            <FadeIn>
              <div className="bg-white p-8 rounded-2xl shadow-lg border border-blue-100">
                <WaitlistStats className="mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                  Få exklusiv förhandsåtkomst och 3 månader gratis
                </h3>
                <p className="text-gray-600 mb-6 text-center">
                  Endast {remainingSpots} platser kvar med {currentTier === 'Grundare' ? '3' : currentTier === 'Pionjär' ? '2' : '1'} månader gratis användning och livstids VIP-support
                </p>
                <WaitlistForm onSuccess={() => setSubmitted(true)} />
              </div>
            </FadeIn>
          ) : (
            <FadeIn>
              <div className="bg-green-50 p-8 rounded-2xl border border-green-100 shadow-lg text-center">
                <div className="flex items-center justify-center gap-3 mb-2">
                  <CheckCircle2 className="text-green-500" size={28} />
                  <p className="text-gray-900 font-semibold text-xl">
                    Grattis! Du är nu registrerad som {currentTier}
                  </p>
                </div>
                <p className="text-gray-600 text-lg">
                  Vi håller dig uppdaterad om projektets utveckling.
                </p>
              </div>
            </FadeIn>
          )}
        </div>
      </section>
    </div>
  );
}

export default About;